import React from 'react';
import {
  Pagination as MuiPagination,
  PaginationProps as MuiPaginationProps,
  PaginationItem,
  PaginationRenderItemParams,
  SelectChangeEvent,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  FirstPage,
  LastPage,
  NavigateBefore,
  NavigateNext,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledPagination = styled(MuiPagination, {
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant?: 'default' | 'outlined' | 'text' }>(
  ({ theme, variant = 'default' }) => ({
    '& .MuiPaginationItem-root': {
      ...(variant === 'outlined' && {
        border: `1px solid ${theme.palette.divider}`,
        '&.Mui-selected': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          border: `1px solid ${theme.palette.primary.main}`,
        },
      }),
      ...(variant === 'text' && {
        '&.Mui-selected': {
          backgroundColor: 'transparent',
          color: theme.palette.primary.main,
          fontWeight: 600,
        },
      }),
    },
  })
);

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
}));

const PageSizeSelector = styled(FormControl)(({ theme }) => ({
  minWidth: 120,
  '& .MuiSelect-select': {
    fontSize: theme.typography.body2.fontSize,
  },
}));

export interface PaginationProps extends Omit<MuiPaginationProps, 'onChange' | 'variant'> {
  /**
   * Current page number (1-based)
   */
  page: number;
  /**
   * Total number of pages
   */
  count: number;
  /**
   * Callback fired when the page is changed
   */
  onChange: (page: number) => void;
  /**
   * Visual variant
   */
  variant?: 'default' | 'outlined' | 'text';
  /**
   * Whether to show page size selector
   */
  showPageSize?: boolean;
  /**
   * Current page size
   */
  pageSize?: number;
  /**
   * Available page size options
   */
  pageSizeOptions?: number[];
  /**
   * Callback fired when page size is changed
   */
  onPageSizeChange?: (pageSize: number) => void;
  /**
   * Whether to show total count information
   */
  showTotal?: boolean;
  /**
   * Total number of items
   */
  totalItems?: number;
  /**
   * Whether to show first/last page buttons
   */
  showFirstLast?: boolean;
  /**
   * Custom labels for accessibility and internationalization
   */
  labels?: {
    firstPage?: string;
    lastPage?: string;
    previousPage?: string;
    nextPage?: string;
    pageSize?: string;
    totalItems?: string;
    showing?: string;
    of?: string;
  };
  /**
   * Compact layout for smaller screens
   */
  compact?: boolean;
}

const defaultLabels = {
  firstPage: 'Go to first page',
  lastPage: 'Go to last page',
  previousPage: 'Go to previous page',
  nextPage: 'Go to next page',
  pageSize: 'Items per page',
  totalItems: 'Total items',
  showing: 'Showing',
  of: 'of',
};

export const Pagination: React.FC<PaginationProps> = ({
  page,
  count,
  onChange,
  variant = 'default',
  showPageSize = false,
  pageSize = 10,
  pageSizeOptions = [5, 10, 25, 50, 100],
  onPageSizeChange,
  showTotal = false,
  totalItems,
  showFirstLast = false,
  labels = defaultLabels,
  compact = false,
  size = 'medium',
  ...props
}) => {
  const mergedLabels = { ...defaultLabels, ...labels };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    onChange(value);
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newPageSize = parseInt(String(event.target.value), 10);
    onPageSizeChange?.(newPageSize);
  };

  const renderPaginationItem = (item: PaginationRenderItemParams) => {
    if (showFirstLast) {
      return (
        <PaginationItem
          slots={{
            first: FirstPage,
            last: LastPage,
            previous: NavigateBefore,
            next: NavigateNext,
          }}
          {...item}
        />
      );
    }
    return <PaginationItem {...item} />;
  };

  const getTotalInfo = () => {
    if (!showTotal || !totalItems) return null;

    const startItem = (page - 1) * pageSize + 1;
    const endItem = Math.min(page * pageSize, totalItems);

    return (
      <Typography variant="body2" color="textSecondary">
        {mergedLabels.showing} {startItem}-{endItem} {mergedLabels.of} {totalItems}
      </Typography>
    );
  };

  if (compact) {
    return (
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <StyledPagination
          page={page}
          count={count}
          onChange={handlePageChange}
          variant={variant === 'default' ? undefined : variant}
          size={size}
          showFirstButton={showFirstLast}
          showLastButton={showFirstLast}
          renderItem={renderPaginationItem}
          {...props}
        />
        {showTotal && getTotalInfo()}
      </Box>
    );
  }

  return (
    <PaginationContainer>
      <Box display="flex" alignItems="center" gap={2}>
        {showPageSize && onPageSizeChange && (
          <PageSizeSelector size="small">
            <InputLabel>{mergedLabels.pageSize}</InputLabel>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              label={mergedLabels.pageSize}
            >
              {pageSizeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </PageSizeSelector>
        )}
        
        {showTotal && getTotalInfo()}
      </Box>

      <StyledPagination
        page={page}
        count={count}
        onChange={handlePageChange}
        variant={variant === 'default' ? undefined : variant}
        size={size}
        showFirstButton={showFirstLast}
        showLastButton={showFirstLast}
        renderItem={renderPaginationItem}
        {...props}
      />
    </PaginationContainer>
  );
}; 